import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const ACCESS_TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

// Public (no-auth) axios instance for unauthenticated endpoints (e.g. share links).
export const publicApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
let isRefreshing = false;
let refreshPromise = null;

const setStoredTokens = (accessToken, refreshToken) => {
  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    api.setAuthToken(accessToken);
  }
  if (typeof refreshToken !== 'undefined') {
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  }
};

const clearStoredAuth = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('currentUser');
  api.setAuthToken(null);
};

const buildAuthHeaders = (includeJsonContentType = true) => {
  const headers = {};
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);

  if (includeJsonContentType) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const fetchWithKeepalive = async (url, { method = 'GET', data } = {}) => {
  const response = await fetch(`${API_URL}${url}`, {
    method,
    headers: buildAuthHeaders(data !== undefined),
    body: data !== undefined ? JSON.stringify(data) : undefined,
    keepalive: true,
  });

  if (response.status === 401) {
    clearStoredAuth();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;

    try {
      const errorData = await response.json();
      errorMessage = errorData?.error || errorData?.message || errorMessage;
    } catch {
      // Keep the default error message when the response body is empty.
    }

    throw new Error(errorMessage);
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return null;
  }

  return response.json();
};

const performTokenRefresh = async () => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return null;

  const response = await axios.post(`${API_URL}/auth/refresh`, { refresh_token: refreshToken }, {
    headers: { 'Content-Type': 'application/json' },
  });

  const newAccessToken = response.data?.token;
  const newRefreshToken = response.data?.refresh_token;

  if (!newAccessToken) return null;
  setStoredTokens(newAccessToken, newRefreshToken ?? null);
  return newAccessToken;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthCall = originalRequest?.url?.includes('/auth/refresh') || originalRequest?.url?.includes('/auth/logout');

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthCall) {
      originalRequest._retry = true;

      try {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = performTokenRefresh()
            .catch(() => null)
            .finally(() => {
              isRefreshing = false;
              refreshPromise = null;
            });
        }

        const newToken = await refreshPromise;
        if (newToken) {
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${newToken}`,
          };
          return api.request(originalRequest);
        }
      } catch {
        // Fall through to logout handling
      }

      clearStoredAuth();
      if (!originalRequest._skipAuthRedirect) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Helper method to set auth token
api.setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

const storedToken = localStorage.getItem(ACCESS_TOKEN_KEY);
if (storedToken) {
  api.setAuthToken(storedToken);
}

api.setSessionTokens = (accessToken, refreshToken) => {
  setStoredTokens(accessToken, refreshToken);
};

api.clearStoredAuth = () => {
  clearStoredAuth();
};

// Authentication API
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  refresh: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
  logout: () => api.post('/auth/logout', {
    refresh_token: localStorage.getItem(REFRESH_TOKEN_KEY),
  }),
  logoutAllDevices: () => api.post('/auth/logout-all-devices'),
  getCurrentUser: () => api.get('/auth/me', { _skipAuthRedirect: true }),
  changePassword: (data) => api.post('/auth/change-password', data),
  updateNotificationSound: (data) => api.patch('/profile/notification-sound', data),
};

// Dashboard API
export const dashboardApi = {
  getDashboardData: () => api.get('/dashboard'),
  getAnalytics: () => api.get('/dashboard/analytics'),
};

// Projects API
export const projectsApi = {
  getAll: (status, showAll = false) => {
    const params = new URLSearchParams();
    if (status && status !== 'all') params.append('status', status);
    if (showAll) params.append('show_all', 'true');
    return api.get(`/projects${params.toString() ? '?' + params.toString() : ''}`);
  },
  getById: (id) => api.get(`/projects/${id}`),
  getItemsForProject: (projectId) => api.get(`/projects/${projectId}/items`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  convertToSalesOrder: (id, data) => api.post(`/projects/${id}/convert-to-sales-order`, data),
  markCompleted: (id) => api.post(`/projects/${id}/mark-completed`),
  revertToSalesOrder: (id) => api.post(`/projects/${id}/revert-to-sales-order`),
  revertToQuote: (id) => api.post(`/projects/${id}/revert-to-quote`),
  duplicate: (id, data) => api.post(`/projects/${id}/duplicate`, data),
};

// Project Items API
export const itemsApi = {
  getByProjectId: (projectId) => api.get(`/projects/${projectId}/items`),
  create: (projectId, data) => api.post(`/projects/${projectId}/items`, data),
  update: (itemId, data) => api.put(`/items/${itemId}`, data),
  delete: (itemId) => api.delete(`/items/${itemId}`),
  bulkSelect: (projectId, data) => api.post(`/projects/${projectId}/items/bulk-select`, data),
  bulkReorder: (projectId, data) => api.post(`/projects/${projectId}/items/bulk-reorder`, data),
};

// Purchase Orders API
export const purchaseOrdersApi = {
  getAll: (includeItems = false) => api.get(`/purchase-orders?include_items=${includeItems}`),
  getByProjectId: (projectId, includeItems = false) => 
    api.get(`/projects/${projectId}/purchase-orders?include_items=${includeItems}`),
  getById: (id, includeItems = true) => 
    api.get(`/purchase-orders/${id}?include_items=${includeItems}`),
  create: (projectId, data) => api.post(`/projects/${projectId}/purchase-orders`, data),
  createStandalone: (data) => api.post('/purchase-orders', data),
  extractItems: (formData) => api.post('/purchase-orders/extract-items', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  addItem: (purchaseOrderId, data) => api.post(`/purchase-orders/${purchaseOrderId}/items`, data),
  updateItem: (itemId, data) => api.put(`/purchase-order-items/${itemId}`, data),
  deleteItem: (itemId) => api.delete(`/purchase-order-items/${itemId}`),
  bulkReorderItems: (purchaseOrderId, data) => api.post(`/purchase-orders/${purchaseOrderId}/items/bulk-reorder`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  exportPDF: (id) => api.get(`/purchase-orders/${id}/export/pdf`, { responseType: 'blob' }),
  markAsComplete: (id) => api.post(`/purchase-orders/${id}/complete`),
  revertToNew: (id) => api.post(`/purchase-orders/${id}/revert-to-new`),
};

// Workers API
export const workersApi = {
  getAll: () => api.get('/workers'),
  getAssignedProjects: (workerId, status = 'Sales Order') => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    const query = params.toString();
    return api.get(`/workers/${workerId}/assigned-projects${query ? `?${query}` : ''}`);
  },
  getByProjectId: (projectId) => api.get(`/projects/${projectId}/workers`),
  create: (data) => api.post('/workers', data),
  update: (id, data) => api.put(`/workers/${id}`, data),
  delete: (id) => api.delete(`/workers/${id}`),
  resetPassword: (id) => api.post(`/workers/${id}/reset-password`),
  assignToProject: (projectId, data) => api.post(`/projects/${projectId}/workers`, data),
  updateProjectWorkers: (projectId, data) => api.put(`/projects/${projectId}/workers/batch`, data),
  removeFromProject: (projectId, workerId) => api.delete(`/projects/${projectId}/workers/${workerId}`),
};

// Activities API
export const activitiesApi = {
  getByProjectId: (projectId, completed, withNotes = false) => {
    let query = '';
    if (completed !== undefined) query += `?completed=${completed}`;
    if (withNotes) query += (query ? '&' : '?') + 'with_notes=true';
    return api.get(`/projects/${projectId}/activities${query}`);
  },
  getByFileId: (fileId) => api.get(`/activities?file_id=${fileId}`),
  getById: (activityId) => api.get(`/activities/${activityId}`),
  create: (projectId, data) => api.post(`/projects/${projectId}/activities`, data),
  update: (id, data) => api.put(`/activities/${id}`, data),
  updateKeepalive: (id, data) => fetchWithKeepalive(`/activities/${id}`, { method: 'PUT', data }),
  markAsComplete: (id) => api.put(`/activities/${id}/complete`),
  markAsIncomplete: (id) => api.put(`/activities/${id}/incomplete`),
  delete: (id) => api.delete(`/activities/${id}`),
  
  // Worker assignments
  assignWorkers: (activityId, workerIds) => api.post(`/activities/${activityId}/workers`, { worker_ids: workerIds }),
  getWorkers: (activityId) => api.get(`/activities/${activityId}/workers`),
  
  // Get all activity details (notes, photos, workers) in one request
  getDetails: (activityId) => api.get(`/activities/${activityId}/details`),
  
  // Activity notes with @mentions
  getNotes: (activityId) => api.get(`/activities/${activityId}/notes`),
  createNote: (activityId, content) => api.post(`/activities/${activityId}/notes`, { content }),
  createNoteWithPhotos: (activityId, formData) => api.post(`/activities/${activityId}/notes`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Photo management
  uploadPhoto: (activityId, formData) => api.post(`/activities/${activityId}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getPhotos: (activityId) => api.get(`/activities/${activityId}/photos`),
  getPhotoUrl: (photoId, expiry = 24) => api.get(`/activity-photos/${photoId}/url?expiry=${expiry}`),
  deletePhoto: (photoId) => api.delete(`/activity-photos/${photoId}`),
  linkFile: (activityId, fileId) => api.post(`/activities/${activityId}/files`, { file_id: fileId }),
  unlinkFile: (activityId, fileId) => api.delete(`/activities/${activityId}/files/${fileId}`),
};

// Personal Activities API (activities not tied to projects)
export const personalActivitiesApi = {
  getAll: (completed) => {
    const query = completed !== undefined ? `?completed=${completed}` : '';
    return api.get(`/personal-activities${query}`);
  },
  create: (data) => api.post('/personal-activities', data),
  update: (id, data) => api.put(`/activities/${id}`, data),
  markAsComplete: (id) => api.put(`/activities/${id}/complete`),
  markAsIncomplete: (id) => api.put(`/activities/${id}/incomplete`),
  delete: (id) => api.delete(`/activities/${id}`)
};

// Files API
export const filesApi = {
  getByProjectId: (projectId, type) => {
    const query = type ? `?type=${type}` : '';
    return api.get(`/projects/${projectId}/files${query}`);
  },
  upload: (projectId, file, type, documentFolderId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    if (documentFolderId !== null && documentFolderId !== undefined && documentFolderId !== '') {
      formData.append('document_folder_id', documentFolderId);
    }
    return api.post(`/projects/${projectId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  download: (fileId) => api.get(`/files/${fileId}`, { responseType: 'blob' }),
  getUrl: (fileId, expiryMinutes = 5) => api.get(`/files/${fileId}/url?expiry_minutes=${expiryMinutes}`),
  delete: (fileId) => api.delete(`/files/${fileId}`),
  rename: (fileId, newName) => api.put(`/files/${fileId}`, { file_name: newName }),
  moveToPhotoFolder: (fileId, photoFolderId = null) => api.patch(
    `/files/${fileId}/photo-folder`,
    { photo_folder_id: photoFolderId }
  ),
  moveToDocumentFolder: (fileId, documentFolderId = null) => api.patch(
    `/files/${fileId}/document-folder`,
    { document_folder_id: documentFolderId }
  ),
  saveDrawing: (fileId, blob, fileName, preserveResolution = false) => {
    const formData = new FormData();
    formData.append('file', blob, fileName);
    if (preserveResolution) formData.append('preserve_resolution', '1');
    return api.post(`/files/${fileId}/save-drawing`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  savePlanStrokes: (fileId, strokes) => api.post(`/files/${fileId}/save-drawing`, {
    format: 'vector-strokes-v1',
    strokes,
  }),
};

export const photoFoldersApi = {
  getByProjectId: (projectId) => api.get(`/projects/${projectId}/photo-folders`),
  create: (projectId, data) => api.post(`/projects/${projectId}/photo-folders`, data),
  rename: (photoFolderId, data) => api.patch(`/photo-folders/${photoFolderId}`, data),
  delete: (photoFolderId) => api.delete(`/photo-folders/${photoFolderId}`),
};

export const documentFoldersApi = {
  getByProjectId: (projectId, type) => {
    const query = type ? `?type=${type}` : '';
    return api.get(`/projects/${projectId}/document-folders${query}`);
  },
  create: (projectId, data) => api.post(`/projects/${projectId}/document-folders`, data),
  rename: (folderId, data) => api.patch(`/document-folders/${folderId}`, data),
  delete: (folderId) => api.delete(`/document-folders/${folderId}`),
};

export const planShareApi = {
  createLink: (fileId) => api.post(`/files/${fileId}/share-link`),
  exportPlanPdf: (fileId) => api.get(`/files/${fileId}/export-plan-pdf`, { responseType: 'blob' }),
  getSharedPlan: (token) => publicApi.get(`/public/plan/${token}`),
  getPhotoUrl: (token, photoId, photoType) => publicApi.get(`/public/plan/${token}/photo-url`, { params: { photo_id: photoId, photo_type: photoType } }),
};

export const guideApi = {
  get: () => publicApi.get('/public/user-guide'),
};

// Blobs API - for direct blob operations using blob_path
export const blobsApi = {
  download: (blobPath) => api.get(`/blobs/download?blob_path=${encodeURIComponent(blobPath)}`, { responseType: 'blob' }),
  versions: (blobPath, cursor) => api.get('/blobs/versions', { params: { blob_path: blobPath, ...(cursor ? { cursor } : {}) } }),
  restoreVersion: (blobPath, versionId, expectedEtag) => api.post('/blobs/restore-version', {
    blob_path: blobPath,
    version_id: versionId,
    expected_etag: expectedEtag,
  }),
  saveDrawing: (blobPath, blob, fileName) => {
    const formData = new FormData();
    formData.append('blob_path', blobPath);
    formData.append('file', blob, fileName);
    return api.post('/blobs/save-drawing', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Forms API
export const formsApi = {
  getByProjectId: (projectId, type, options = {}) => {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (options.summary) params.append('summary', 'true');
    const query = params.toString();
    return api.get(`/projects/${projectId}/forms${query ? `?${query}` : ''}`);
  },
  upload: (projectId, file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    return api.post(`/projects/${projectId}/forms`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  download: (formId) => api.get(`/forms/${formId}`, { responseType: 'blob' }),
  getUrl: (formId, expiry = 24) => api.get(`/forms/${formId}/url?expiry=${expiry}`),
  delete: (formId) => api.delete(`/forms/${formId}`),
};

// Notifications API
export const notificationsApi = {
  getAll: (unreadOnly = false, limit = 50) => 
    api.get(`/notifications?unread_only=${unreadOnly}&limit=${limit}`),
  getSummary: () => api.get('/notifications/summary'),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
  clearAll: () => api.delete('/notifications/clear-all'),
};

// Project Notes API
export const projectNotesApi = {
  getByProjectId: (projectId, limit = 100, offset = 0) => 
    api.get(`/projects/${projectId}/notes?limit=${limit}&offset=${offset}`),
  create: (projectId, data) => api.post(`/projects/${projectId}/notes`, data),
  createWithPhotos: (projectId, formData) => api.post(`/projects/${projectId}/notes`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Form Templates API
export const formTemplatesApi = {
  getAll: () => api.get('/form-templates'),
  getById: (templateId) => api.get(`/form-templates/${templateId}`),
  create: (data) => api.post('/form-templates', data),
  update: (templateId, data) => api.put(`/form-templates/${templateId}`, data),
  delete: (templateId) => api.delete(`/form-templates/${templateId}`),
};

// Project Forms API
export const projectFormsApi = {
  getByProjectId: (projectId, options = {}) =>
    api.get(`/projects/${projectId}/forms`, {
      params: options.summary ? { summary: true } : undefined,
    }),
  getById: (projectId, formId) => api.get(`/projects/${projectId}/forms/${formId}`),
  create: (projectId, data) => api.post(`/projects/${projectId}/forms`, data),
  update: (projectId, formId, data) => api.put(`/projects/${projectId}/forms/${formId}`, data),
  linkActivity: (projectId, formId, activityId) => api.post(`/projects/${projectId}/forms/${formId}/activities/${activityId}`),
  unlinkActivity: (projectId, formId, activityId) => api.delete(`/projects/${projectId}/forms/${formId}/activities/${activityId}`),
  delete: (projectId, formId) => api.delete(`/projects/${projectId}/forms/${formId}`),
  exportPDF: (projectId, formId) => api.get(`/projects/${projectId}/forms/${formId}/export/pdf`, {
    responseType: 'blob'
  }),
  
  // Form photo management
  uploadPhoto: (projectId, formId, formData) => api.post(`/projects/${projectId}/forms/${formId}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  linkPhoto: (projectId, formId, data) => api.post(`/projects/${projectId}/forms/${formId}/photos/link`, data),
  getPhotos: (projectId, formId) => api.get(`/projects/${projectId}/forms/${formId}/photos`),
  getPhotoUrl: (photoId, expiry = 24) => api.get(`/form-photos/${photoId}/url?expiry=${expiry}`),
  deletePhoto: (photoId) => api.delete(`/form-photos/${photoId}`),
};

// Certificates API
export const certificatesApi = {
  getByWorkerId: (workerId) => api.get(`/workers/${workerId}/certificates`),
  upload: (workerId, formData) => api.post(`/workers/${workerId}/certificates`, formData),
  getExpiring: (days = 30) => api.get(`/certificates/expiring?days=${days}`),
  getUrl: (certificateId, expiry = 24) => api.get(`/certificates/${certificateId}/url?expiry=${expiry}`),
};

// Saved Items API
export const savedItemsApi = {
  getAll: () => api.get('/saved-items'),
  search: (query) => api.get(`/saved-items/search?q=${encodeURIComponent(query)}`),
  create: (data) => api.post('/saved-items', data),
  update: (id, data) => api.put(`/saved-items/${id}`, data),
  delete: (id) => api.delete(`/saved-items/${id}`),
};

// PDF Export API
export const pdfExportApi = {
  exportQuote: (projectId, data = {}) => api.post(`/projects/${projectId}/export/quote-pdf`, data, {
    responseType: 'blob'
  }),
  exportSalesOrder: (projectId) => api.post(`/projects/${projectId}/export/sales-order-pdf`, {}, {
    responseType: 'blob'
  }),
  getDefaultTerms: () => api.get('/config/terms-and-conditions'),
};

// RAMS API
export const ramsApi = {
  getByProjectId: (projectId) => api.get(`/projects/${projectId}/rams`),
  getSignatures: (projectId) => api.get(`/projects/${projectId}/ram-signatures`),
  getRamStatus: (projectId) => api.get(`/projects/${projectId}/ram-status`),
  getProjectWorkers: (projectId) => api.get(`/projects/${projectId}/workers`),
  upload: (projectId, formData) => api.post(`/projects/${projectId}/rams`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getDownloadUrl: (ramId, expiry = 24) => api.get(`/rams/${ramId}/url?expiry=${expiry}`),
  submitSignature: (ramId, data) => api.post(`/rams/${ramId}/signatures`, data),
  delete: (ramId) => api.delete(`/rams/${ramId}`),
};

// Generated RAMS API (LLM-powered)
export const generatedRamsApi = {
  getByProjectId: (projectId) => api.get(`/projects/${projectId}/generated-rams`),
  getById: (id) => api.get(`/generated-rams/${id}`),
  getContext: (projectId) => api.get(`/projects/${projectId}/rams-generator/context`),
  start: (projectId, data) => api.post(`/projects/${projectId}/rams-generator/start`, data),
  generate: (projectId, data) => api.post(`/projects/${projectId}/rams-generator/generate`, data),
  save: (projectId, data) => api.post(`/projects/${projectId}/generated-rams`, data),
  update: (id, data) => api.put(`/generated-rams/${id}`, data),
  revise: (id, data) => api.post(`/generated-rams/${id}/revise`, data),
  savePdf: (id) => api.post(`/generated-rams/${id}/save-pdf`),
  delete: (id) => api.delete(`/generated-rams/${id}`),
};

// Organisations API
export const organisationsApi = {
  getAll: () => api.get('/organisations'),
  getById: (id) => api.get(`/organisations/${id}`),
  create: (data) => api.post('/organisations', data),
  update: (id, data) => api.put(`/organisations/${id}`, data),
  delete: (id) => api.delete(`/organisations/${id}`),
  search: (query) => api.get(`/organisations/search?q=${encodeURIComponent(query)}`),
  getContacts: (organisationId) => api.get(`/organisations/${organisationId}/contacts`),
};

// Vendors API
export const vendorsApi = {
  getAll: () => api.get('/vendors'),
  getById: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
  search: (query) => api.get(`/vendors/search?q=${encodeURIComponent(query)}`),
};

// Organisation Contacts API
export const organisationContactsApi = {
  getAll: () => api.get('/organisation-contacts'),
  getById: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/organisation-contacts', data),
  update: (id, data) => api.put(`/organisation-contacts/${id}`, data),
  delete: (id) => api.delete(`/organisation-contacts/${id}`),
  search: (query) => api.get(`/organisation-contacts/search?q=${encodeURIComponent(query)}`),
};

// AI Assistant API
export const assistantApi = {
  chat: (message, conversationId = 'default') => api.post('/assistant/chat', { 
    message, 
    conversation_id: conversationId 
  }),
  chatWithFiles: (formData) => api.post('/assistant/chat/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getConversations: (limit = 20) => api.get(`/assistant/conversations?limit=${limit}`),
  getConversationHistory: (conversationId) => api.get(`/assistant/conversations/${conversationId}/history`),
};

export const quoteAssistApi = {
  getContext: (projectId) => api.get(`/projects/${projectId}/quote-assist/context`),
  start: (projectId, data) => api.post(`/projects/${projectId}/quote-assist/start`, data),
  convertPdf: (projectId, formData) => api.post(`/projects/${projectId}/quote-assist/pdf-to-images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  continueQuestions: (projectId, data) => api.post(`/projects/${projectId}/quote-assist/questions`, data),
  generate: (projectId, data) => api.post(`/projects/${projectId}/quote-assist/generate`, data),
  review: (projectId) => api.post(`/projects/${projectId}/quote-assist/review`),
};

// Survey Notes API
export const surveyNotesApi = {
  get: (projectId) => api.get(`/projects/${projectId}/survey-notes`),
  save: (projectId, notes, source = 'desktop') =>
    api.post(`/projects/${projectId}/survey-notes`, { notes, source }),
  getHistory: (projectId, limit = 20, offset = 0) =>
    api.get(`/projects/${projectId}/survey-notes/history?limit=${limit}&offset=${offset}`),
};

// Tools API
export const toolsApi = {
  extractPlantSchedule: (formData) => api.post('/tools/plant-schedule/extract', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Tenant API
export const tenantApi = {
  getBranding: () => api.get('/tenant/branding'),
};

// Location API
export const locationApi = {
  getWhat3Words: (lat, lng) => api.get(`/location/what3words?lat=${lat}&lng=${lng}`),
  getGeocode: (lat, lng) => api.get(`/location/geocode?lat=${lat}&lng=${lng}`),
};

// Time Entries API
export const timeEntriesApi = {
  // Get current clock status
  getStatus: () => api.get('/time-entries/status'),
  
  // Clock in (requires location verification)
  clockIn: (data) => api.post('/time-entries/clock-in', data),
  
  // Clock out (requires location verification)
  clockOut: (data) => api.post('/time-entries/clock-out', data),
  
  // Create manual entry (forgot to check in/out)
  createManual: (data) => api.post('/time-entries/manual', data),
  
  // Get user's own entries
  getMyEntries: (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const query = params.toString();
    return api.get(`/time-entries/my-entries${query ? `?${query}` : ''}`);
  },
  
  // Get user's totals
  getMyTotals: (startDate, endDate) => 
    api.get(`/time-entries/my-totals?start_date=${startDate}&end_date=${endDate}`),
  
  // Update a time entry
  update: (entryId, data) => api.put(`/time-entries/${entryId}`, data),
  
  // Delete a time entry (Office only)
  delete: (entryId) => api.delete(`/time-entries/${entryId}`),
  
  // Admin endpoints (Office role required)
  getPending: () => api.get('/time-entries/pending'),
  getQueried: () => api.get('/time-entries/queried'),
  
  approve: (entryId) => api.post(`/time-entries/${entryId}/approve`),
  query: (entryId, data) => api.post(`/time-entries/${entryId}/query`, data),
  
  getAll: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);
    if (params.worker_id) searchParams.append('worker_id', params.worker_id);
    const query = searchParams.toString();
    return api.get(`/time-entries/all${query ? `?${query}` : ''}`);
  },
  
  getTotals: (period = 'weekly', numPeriods = 3) => 
    api.get(`/time-entries/totals?period=${period}&num_periods=${numPeriods}`),
  
  getWorkerEntries: (workerId, startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const query = params.toString();
    return api.get(`/time-entries/worker/${workerId}${query ? `?${query}` : ''}`);
  },
};

// Leave Requests API. These records are separate from worked time entries.
export const leaveRequestsApi = {
  create: (data) => api.post('/leave-requests', data),
  getMyRequests: (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const query = params.toString();
    return api.get(`/leave-requests/my-requests${query ? `?${query}` : ''}`);
  },
  update: (requestId, data) => api.put(`/leave-requests/${requestId}`, data),
  cancel: (requestId) => api.post(`/leave-requests/${requestId}/cancel`),
  getAll: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append('status', params.status);
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);
    const query = searchParams.toString();
    return api.get(`/leave-requests/all${query ? `?${query}` : ''}`);
  },
  getAnnualSummary: (year) => api.get(`/leave-requests/annual-summary?year=${year}`),
  approve: (requestId, managerNote = '') =>
    api.post(`/leave-requests/${requestId}/approve`, { manager_note: managerNote }),
  query: (requestId, reason) =>
    api.post(`/leave-requests/${requestId}/query`, { reason }),
  reject: (requestId, managerNote) =>
    api.post(`/leave-requests/${requestId}/reject`, { manager_note: managerNote }),
};

const apiExports = {
  ...api,
  authApi,
  dashboardApi,
  projectsApi,
  itemsApi,
  purchaseOrdersApi,
  workersApi,
  activitiesApi,
  personalActivitiesApi,
  filesApi,
  blobsApi,
  formsApi,
  notificationsApi,
  projectNotesApi,
  formTemplatesApi,
  projectFormsApi,
  certificatesApi,
  savedItemsApi,
  pdfExportApi,
  ramsApi,
  organisationsApi,
  vendorsApi,
  organisationContactsApi,
  assistantApi,
  quoteAssistApi,
  surveyNotesApi,
  toolsApi,
  tenantApi,
  locationApi,
  timeEntriesApi,
  leaveRequestsApi,
  guideApi,
};

export default apiExports;
